import React, { lazy } from 'react';
import { ArrowRightIcon, CalendarDaysIcon, MapPinIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
type Trip = {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  duration: string;
  elevation: string;
  price: string;
  route: string;
  action: string;
};
const trips: Trip[] = [{
  title: 'Manaslu Circuit Trek',
  description: 'Our signature high-mountain traverse through river gorges, Nubri villages, and the immense horizon of Larkya La.',
  image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=1200&q=85',
  imageAlt: 'Snow-covered Himalayan peaks rising above the Manaslu region',
  duration: '10 days',
  elevation: '5,106m',
  price: 'From US$1,165 pp',
  route: '/routes/manaslu-circuit',
  action: 'Explore the circuit'
}, {
  title: 'Tsum Valley & Manaslu',
  description: 'A slower, culturally rich extension combining the meditative side valleys of Tsum with the classic Manaslu route.',
  image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=1200&q=85',
  imageAlt: 'Layered Himalayan mountains beyond a remote valley',
  duration: '17–19 days',
  elevation: '5,106m',
  price: 'From US$2,190 pp',
  route: '/plan-your-trek',
  action: 'Plan this journey'
}, {
  title: 'Manaslu Base Camp Explorer',
  description: 'A private, acclimatisation-led exploration designed around close glacier views, high-valley walks, and flexible timing.',
  image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=85',
  imageAlt: 'Jagged snow-covered Himalayan ridge under a clear sky',
  duration: '12–14 days',
  elevation: '4,800m+',
  price: 'From US$1,390 pp',
  route: '/plan-your-trek',
  action: 'Build a private plan'
}];
export function TripGrid() {
  return <section id="routes" className="w-full bg-[#e9e3d5] py-20 text-[#1d2d2a] sm:py-28" aria-labelledby="routes-title" data-id="element-326" data-mp="32smwf5mkwn30">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10" data-id="element-327" data-mp="avgrvhxalu1">
        <header className="flex flex-col gap-6 border-b border-[#bcb3a1] pb-10 lg:flex-row lg:items-end lg:justify-between" data-id="element-328" data-mp="d7ws344em4g">
          <div className="max-w-3xl" data-id="element-329" data-mp="a9b73w94lr2h">
            <p className="eyebrow" data-id="element-330" data-mp="aacp6kok7dtp">Choose your route</p>
            <h2 id="routes-title" className="mt-4 font-display text-4xl leading-tight sm:text-5xl" data-id="element-331" data-mp="f8e36r74omyb">
              Three ways to walk with Manaslu.
            </h2>
          </div>
          <p className="max-w-xl text-sm leading-7 text-[#55615a]" data-id="element-332" data-mp="o07qqxl2qxxe">
            The classic circuit is available as a defined departure. Our Tsum
            Valley and base camp journeys are individually planned around the
            season, your experience, and trail conditions.
          </p>
        </header>

        <div className="mt-12 grid gap-7 lg:grid-cols-3" data-id="element-333" data-mp="12z1qajpm7j5g">
          {trips.map(trip => <article key={trip.title} className="flex flex-col overflow-hidden border border-[#c8c1af] bg-[#f7f4ec]" data-id="element-334" data-mp="2ngauvxuvy4ko">
              <img src={trip.image} alt={trip.imageAlt} className="h-64 w-full object-cover sm:h-72" loading="lazy" data-id="element-335" data-mp="2ijaplp5v15um" />
              <div className="flex flex-1 flex-col p-6 sm:p-7" data-id="element-336" data-mp="28l4rlin6cjv1">
                <h3 className="font-display text-3xl leading-tight" data-id="element-337" data-mp="365zckp9q7oi3">
                  {trip.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-[#55615a]" data-id="element-338" data-mp="319iofv2j1a50">
                  {trip.description}
                </p>

                <dl className="mt-7 grid grid-cols-2 gap-4 border-y border-[#c8c1af] py-4 text-sm" data-id="element-339" data-mp="2rhw51bg1c4r6">
                  <div data-id="element-340" data-mp="3owqithyf2ys9">
                    <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#6D5A39]" data-id="element-341" data-mp="3oxa498na05b1">
                      <CalendarDaysIcon size={14} aria-hidden="true" data-id="element-342" data-mp="3k4qoq651303l" />
                      Duration
                    </dt>
                    <dd className="mt-2 text-[#46534c]" data-id="element-343" data-mp="34qmy0zxa1zsn">{trip.duration}</dd>
                  </div>
                  <div data-id="element-344" data-mp="39l0l8f1sd0m8">
                    <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#6D5A39]" data-id="element-345" data-mp="2uqck1d3f4ocf">
                      <MapPinIcon size={14} aria-hidden="true" data-id="element-346" data-mp="2zq8o1cc48frz" />
                      High point
                    </dt>
                    <dd className="mt-2 text-[#46534c]" data-id="element-347" data-mp="3nfa2phbjkn5p">{trip.elevation}</dd>
                  </div>
                </dl>

                <div className="mt-5" data-id="element-348" data-mp="3sfxonokwydt2">
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#6D5A39]" data-id="element-349" data-mp="3dpi50e85bdx0">
                    Trek price
                  </p>
                  <p className="mt-1 font-display text-2xl text-[#1d2d2a]" data-id="element-350" data-mp="3im8l31jya9if">
                    {trip.price}
                  </p>
                </div>

                <Link to={trip.route} className="mt-7 inline-flex w-fit items-center gap-2 text-sm font-bold text-[#1d2d2a] transition-colors hover:text-[#6D5A39] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A48C5A] focus-visible:ring-offset-4" data-id="element-351" data-mp="ezhv0lz3nrdc">
                  {trip.action}
                  <ArrowRightIcon size={16} aria-hidden="true" data-id="element-352" data-mp="29edptir4dqru" />
                </Link>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}